import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

// Dispatched on document once every section on the page shows a newly loaded (combined-listing) product.
const SECTIONS_REFRESHED_EVENT = 'product:sections-refreshed';

const PRODUCT_SECTION_PREFETCH_TTL = 30_000;
const PRODUCT_SECTION_PREFETCH_LIMIT = 4;
const PRODUCT_SECTION_HOVER_DELAY = 80;

/**
 * @typedef {object} ProductSectionPrefetch
 * @property {number} createdAt
 * @property {Promise<string>} promise
 * @property {Document | undefined} html
 * @property {HTMLImageElement | undefined} image
 */

/** @type {Map<string, ProductSectionPrefetch>} */
const productSectionPrefetchCache = new Map();

/**
 * Returns a non-expired prefetched product section.
 * @param {string} requestUrl
 * @returns {ProductSectionPrefetch | undefined}
 */
function getProductSectionPrefetch(requestUrl) {
  const entry = productSectionPrefetchCache.get(requestUrl);
  if (!entry) return undefined;

  if (Date.now() - entry.createdAt > PRODUCT_SECTION_PREFETCH_TTL) {
    productSectionPrefetchCache.delete(requestUrl);
    return undefined;
  }

  // Refresh recency so the bounded cache evicts the least recently used entry.
  productSectionPrefetchCache.delete(requestUrl);
  productSectionPrefetchCache.set(requestUrl, entry);
  return entry;
}

/**
 * Starts loading the responsive hero image from a prefetched product section.
 * @param {Document} html
 * @returns {HTMLImageElement | undefined}
 */
function preloadPrimaryProductImage(html) {
  const source = /** @type {HTMLImageElement | null} */ (html.querySelector('img[fetchpriority="high"]'));
  if (!source) return undefined;

  const image = new Image();
  image.fetchPriority = 'high';
  image.decoding = 'async';

  const sizes = source.getAttribute('sizes');
  const srcset = source.getAttribute('srcset');
  if (sizes) image.sizes = sizes;
  if (srcset) image.srcset = srcset;
  image.src = source.getAttribute('src') || '';
  return image;
}

/**
 * Prefetches one exact section response and keeps it briefly for the real selection.
 * @param {string} requestUrl
 * @returns {ProductSectionPrefetch}
 */
function prefetchProductSection(requestUrl) {
  const existing = getProductSectionPrefetch(requestUrl);
  if (existing) return existing;

  while (productSectionPrefetchCache.size >= PRODUCT_SECTION_PREFETCH_LIMIT) {
    const oldestKey = productSectionPrefetchCache.keys().next().value;
    if (!oldestKey) break;
    productSectionPrefetchCache.delete(oldestKey);
  }

  /** @type {ProductSectionPrefetch} */
  const entry = {
    createdAt: Date.now(),
    html: undefined,
    image: undefined,
    promise: Promise.resolve(''),
  };

  entry.promise = fetch(requestUrl)
    .then((response) => {
      if (!response.ok) throw new Error(`Product section prefetch failed: ${response.status}`);
      return response.text();
    })
    .then((responseText) => {
      entry.html = new DOMParser().parseFromString(responseText, 'text/html');
      entry.image = preloadPrimaryProductImage(entry.html);
      return responseText;
    })
    .catch((error) => {
      if (productSectionPrefetchCache.get(requestUrl) === entry) {
        productSectionPrefetchCache.delete(requestUrl);
      }
      throw error;
    });

  productSectionPrefetchCache.set(requestUrl, entry);
  return entry;
}

/**
 * Lets the prefetched hero reach the screen before the heavier section morph starts.
 * @returns {Promise<void>}
 */
function waitForPrefetchedHeroPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => setTimeout(resolve, 50));
  });
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {AbortController | undefined} */
  #sectionsAbortController;

  /** @type {number | undefined} */
  #prefetchHoverTimer;

  /** @type {HTMLInputElement | undefined} */
  #prefetchHoverOption;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('pointerover', this.queueProductPrefetch);
    this.addEventListener('pointerout', this.cancelQueuedProductPrefetch);
    this.addEventListener('pointerdown', this.prefetchConnectedProduct);
    this.addEventListener('focusin', this.prefetchConnectedProduct);
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerover', this.queueProductPrefetch);
    this.removeEventListener('pointerout', this.cancelQueuedProductPrefetch);
    this.removeEventListener('pointerdown', this.prefetchConnectedProduct);
    this.removeEventListener('focusin', this.prefetchConnectedProduct);
    clearTimeout(this.#prefetchHoverTimer);
    this.#resizeObserver.disconnect();
  }

  /**
   * Finds the connected-product radio associated with an interaction target.
   * @param {EventTarget | null} target
   * @returns {HTMLInputElement | undefined}
   */
  #getConnectedProductOption(target) {
    if (!(target instanceof Element)) return undefined;

    const direct = target.closest('input[data-connected-product-url]');
    if (direct instanceof HTMLInputElement) return direct;

    const nested = target.closest('label')?.querySelector('input[data-connected-product-url]');
    return nested instanceof HTMLInputElement ? nested : undefined;
  }

  /**
   * Builds the exact section URL that the change event will request.
   * @param {HTMLInputElement} selectedOption
   * @returns {string | undefined}
   */
  #buildProductPrefetchUrl(selectedOption) {
    const connectedProductUrl = selectedOption.dataset.connectedProductUrl;
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const { sectionId } = this.dataset;

    if (
      this.dataset.templateProductMatch !== 'true' ||
      !connectedProductUrl ||
      connectedProductUrl === currentUrl ||
      !sectionId ||
      this.closest('featured-product-information') ||
      selectedOption.dataset.optionAvailable === 'false'
    ) {
      return undefined;
    }

    const fieldsetIndex = Number.parseInt(selectedOption.dataset.fieldsetIndex || '');
    const optionValueId = selectedOption.dataset.optionValueId;
    const selectedValues = [...this.selectedOptionsValues];

    if (Number.isNaN(fieldsetIndex) || !optionValueId || !selectedValues.length) return undefined;
    selectedValues[fieldsetIndex] = optionValueId;

    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);
    params.push(`option_values=${selectedValues.join(',')}`);

    return `${connectedProductUrl}?${params.join('&')}&section_id=${sectionId}`;
  }

  /**
   * Shows the already-loaded hero immediately while the complete product section catches up.
   * @param {Document} html
   * @param {string} morphElementSelector
   */
  #showPrefetchedHero(html, morphElementSelector) {
    const currentSection = this.closest(morphElementSelector);
    const nextSection = html.querySelector(morphElementSelector);
    const currentHero = currentSection?.querySelector('img[fetchpriority="high"]');
    const nextHero = nextSection?.querySelector('img[fetchpriority="high"]');

    if (!(currentHero instanceof HTMLImageElement) || !(nextHero instanceof HTMLImageElement)) return;

    for (const attribute of ['src', 'srcset', 'sizes', 'alt', 'width', 'height']) {
      const value = nextHero.getAttribute(attribute);
      if (value === null) currentHero.removeAttribute(attribute);
      else currentHero.setAttribute(attribute, value);
    }
  }

  /**
   * Prefetches a connected product immediately for focus, pointer-down or a settled hover.
   * @param {Event | HTMLInputElement} source
   */
  prefetchConnectedProduct(source) {
    const option =
      source instanceof HTMLInputElement ? source : this.#getConnectedProductOption(source.target);
    if (!option || option.checked) return;

    const requestUrl = this.#buildProductPrefetchUrl(option);
    if (!requestUrl) return;

    prefetchProductSection(requestUrl).promise.catch(() => {});
  }

  /**
   * Starts a bounded prefetch only after the pointer settles on one colour.
   * @param {PointerEvent} event
   */
  queueProductPrefetch(event) {
    const option = this.#getConnectedProductOption(event.target);
    if (!option || option.checked || option === this.#prefetchHoverOption) return;

    clearTimeout(this.#prefetchHoverTimer);
    this.#prefetchHoverOption = option;
    this.#prefetchHoverTimer = window.setTimeout(() => {
      this.prefetchConnectedProduct(option);
    }, PRODUCT_SECTION_HOVER_DELAY);
  }

  /**
   * Cancels a hover intent when the pointer leaves that colour.
   * @param {PointerEvent} event
   */
  cancelQueuedProductPrefetch(event) {
    const option = this.#getConnectedProductOption(event.target);
    if (!option || option !== this.#prefetchHoverOption) return;

    const label = option.closest('label');
    if (label && event.relatedTarget instanceof Node && label.contains(event.relatedTarget)) return;

    clearTimeout(this.#prefetchHoverTimer);
    this.#prefetchHoverTimer = undefined;
    this.#prefetchHoverOption = undefined;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Combined-listing options load a different product: morph this whole product section for it. Other
    // product-dependent sections are refreshed afterwards in the background (see #refreshOtherSections).
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    // A change made while a product switch is still loading is requested against that new product, so it completes the switch.
    const hasPendingProductSwitch =
      isOnProductPage && !!this.#pendingRequestUrl && this.#pendingRequestUrl.split('?')[0] !== currentUrl;
    const isProductSwitch = (loadsNewProduct || hasPendingProductSwitch) && !isOnFeaturedProductSection;
    const { sectionId } = this.dataset;

    const morphElementSelector = isOnFeaturedProductSection
      ? 'featured-product-information'
      : isProductSwitch
      ? sectionId
        ? `#shopify-section-${sectionId}`
        : 'main'
      : undefined;

    const isUnavailableOption =
      selectedOption instanceof HTMLInputElement && selectedOption.dataset.optionAvailable === 'false';

    // Product-page variant changes only render this section, never the full page, to keep the request fast.
    let requestUrl = this.buildRequestUrl(selectedOption);
    if (isOnProductPage && sectionId && !requestUrl.includes('section_id=')) {
      requestUrl += `&section_id=${sectionId}`;
    }

    this.fetchUpdatedSection(requestUrl, morphElementSelector, isUnavailableOption, isProductSwitch);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    // connectedProductUrl is only set on combined-listing radio inputs.
    // Do not suppress it when the option is unavailable – the morph still
    // needs to fetch the correct product URL so the page updates properly.
    const connectedProductUrl = selectedOption.dataset.connectedProductUrl;
    let productUrl = connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
      'dialog-component[data-section-id]': this.closest('dialog-component[data-section-id]')?.dataset.sectionId,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {boolean} [skipVariantPickerMorph] - When true, skips morphing the variant picker to avoid corrupting product state for unavailable combined-listing options.
   * @param {boolean} [isProductSwitch] - When true, the response is a different product (combined listing) replacing the product section.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, skipVariantPickerMorph = false, isProductSwitch = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const { signal } = this.#abortController;
    const prefetched = getProductSectionPrefetch(requestUrl);
    const responsePromise = prefetched
      ? prefetched.promise
      : fetch(requestUrl, { signal }).then((response) => (response.ok ? response.text() : ''));

    responsePromise
      .then(async (responseText) => {
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

        if (prefetched?.html && morphElementSelector && isProductSwitch) {
          this.#showPrefetchedHero(prefetched.html, morphElementSelector);
          await waitForPrefetchedHeroPaint();
          if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        }

        return responseText;
      })
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = prefetched?.html || new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) {
          // The new product may use a template without this section; load it as a normal page instead.
          if (isProductSwitch) {
            const pageUrl = new URL(requestUrl, window.location.origin);
            pageUrl.searchParams.delete('section_id');
            window.location.assign(pageUrl.toString());
          }
          return;
        }

        let newProduct;
        let mainMorphed = false;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
          mainMorphed = true;
          document.dispatchEvent(new CustomEvent(SECTIONS_REFRESHED_EVENT));
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
          if (isProductSwitch) {
            mainMorphed = true;
            this.#refreshOtherSections(requestUrl.split('?')[0]);
          }
        } else if (!skipVariantPickerMorph) {
          newProduct = this.updateVariantPicker(html);
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
              mainMorphed,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
    // Hydrate lazy images injected by the morph. Use rAF so we run after
    // component updatedCallback microtasks have had a chance to settle.
    requestAnimationFrame(() => {
      if (typeof window.runLazyImages === 'function') window.runLazyImages();
    });
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
    // Hydrate lazy images injected by the morph. Use rAF so we run after
    // component updatedCallback microtasks have had a chance to settle.
    requestAnimationFrame(() => {
      if (typeof window.runLazyImages === 'function') window.runLazyImages();
    });
  }

  /**
   * Re-renders the page's other sections for a newly loaded product, in the background, so the
   * product section swap itself stays a single section-only request.
   * @param {string} productUrl - The new product's URL.
   */
  #refreshOtherSections(productUrl) {
    this.#sectionsAbortController?.abort();
    this.#sectionsAbortController = new AbortController();
    const { signal } = this.#sectionsAbortController;

    const productSectionId = `shopify-section-${this.dataset.sectionId}`;
    const configuredSuffixes = (this.dataset.backgroundRefreshSections || '')
      .split(',')
      .map((suffix) => suffix.trim())
      .filter(Boolean);
    const sectionIds = Array.from(document.querySelectorAll('main .shopify-section'))
      .map((section) => section.id)
      .filter((id) => id.startsWith('shopify-section-') && id !== productSectionId)
      .filter(
        (id) =>
          configuredSuffixes.length === 0 || configuredSuffixes.some((suffix) => id.endsWith(`__${suffix}`))
      )
      .map((id) => id.replace('shopify-section-', ''));

    const requests = [];

    // Carry the view parameter through, so an alternate-template (?view=) A/B test keeps
    // rendering its own template when the background section refresh runs.
    const viewParamValue = getViewParameterValue();
    const viewParam = viewParamValue ? `&view=${encodeURIComponent(viewParamValue)}` : '';

    // The Section Rendering API accepts at most 5 sections per request.
    for (let i = 0; i < sectionIds.length; i += 5) {
      const batch = sectionIds.slice(i, i + 5);

      requests.push(
        fetch(`${productUrl}?sections=${batch.join(',')}${viewParam}`, { signal })
          .then((response) => (response.ok ? response.json() : {}))
          .then((sections) => {
            for (const id of batch) {
              const current = document.getElementById(`shopify-section-${id}`);
              const next =
                sections[id] &&
                new DOMParser().parseFromString(sections[id], 'text/html').getElementById(`shopify-section-${id}`);
              if (current && next) morph(current, next);
            }

            requestAnimationFrame(() => {
              if (typeof window.runLazyImages === 'function') window.runLazyImages();
            });
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          })
      );
    }

    Promise.all(requests).then(() => {
      if (!signal.aborted) document.dispatchEvent(new CustomEvent(SECTIONS_REFRESHED_EVENT));
    });
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
